import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { cn } from '@antimatterhq/ui/lib/utils';
import {
  formatDateRange,
  parseManualInputInTimezone,
} from '@antimatterhq/ui/utils/zonedDateTimeUtils';
import { toTimeZone } from '@internationalized/date';
import { TimezonePicker } from '../../timezonePicker/timezonePicker';
import { DateRange } from '../types';

export interface DateTimeRangePickerInputProps {
  placeholder?: string;
  className?: string;
  onClick?: () => void;
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  value: DateRange;
  onChange?: (value: DateRange) => void;
  open: boolean;
  error: string;
  onParseError?: (input: string, error: Error) => void;
}

export const DateTimeRangePickerInput = ({
  placeholder = 'Select date and time range',
  className,
  onClick,
  timezone,
  onTimezoneChange,
  value,
  onChange,
  open,
  error,
  onParseError,
}: DateTimeRangePickerInputProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Format the current value for display
  const formattedValue = useMemo(() => {
    if (!value) return '';
    return formatDateRange(value, timezone);
  }, [value, timezone]);

  // Display text logic: show edit value when editing, otherwise show formatted value
  const displayText = isEditing ? editValue : formattedValue;

  // When the input loses focus or when we need to reset
  const resetToOriginal = useCallback(() => {
    setIsEditing(false);
    setEditValue('');
  }, []);

  // Handle input change - just update the edit value
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsEditing(true);
      setEditValue(e.target.value);
    },
    [],
  );

  // Handle input click - always open popover when clicking the container
  const handleInputClick = useCallback(() => {
    // Always call onClick to open the popover
    onClick?.();

    // Focus the input after a brief delay to ensure popover is open
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [onClick]);

  // Handle focus - track focus state
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  // Handle blur - validate and parse input
  const handleInputBlur = useCallback(() => {
    setIsFocused(false);

    // If we have edited content, try to parse it
    if (isEditing && editValue.trim()) {
      try {
        // Parse the input in the selected timezone
        const parsedRange = parseManualInputInTimezone(
          editValue.trim(),
          timezone,
        );

        // Convert to UTC for storage
        const parsedRangeUtc: DateRange = {
          start: toTimeZone(parsedRange.start, 'UTC'),
          end: toTimeZone(parsedRange.end, 'UTC'),
        };

        // Valid input - update the value in UTC
        onChange?.(parsedRangeUtc);
        resetToOriginal();
      } catch (err) {
        // Parsing error - revert to original value
        onParseError?.(editValue.trim(), err as Error);
        resetToOriginal();
      }
    } else {
      // No changes or empty input - just reset
      resetToOriginal();
    }
  }, [isEditing, editValue, timezone, onChange, resetToOriginal, onParseError]);

  // Handle key presses
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        // Trigger blur to validate
        inputRef.current?.blur();
      } else if (e.key === 'Escape') {
        // Escape key - revert to original value immediately
        resetToOriginal();
        inputRef.current?.blur();
      }
    },
    [resetToOriginal],
  );

  // Handle timezone change
  const handleTimezoneChange = useCallback(
    (newTimezone: string) => {
      onTimezoneChange(newTimezone);
      // Reset any edits when timezone changes
      resetToOriginal();
    },
    [onTimezoneChange, resetToOriginal],
  );

  // Reset edit state when popover closes
  useEffect(() => {
    if (!open) {
      resetToOriginal();
    }
  }, [open, resetToOriginal]);

  // Prevent input click from bubbling when popover is already open
  const handleInputClickCapture = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      // Only prevent bubbling if popover is open AND we're already focused
      // This allows the first click to open the popover
      if (open && isFocused) {
        e.stopPropagation();
      }
    },
    [open, isFocused],
  );

  return (
    <div className={cn('relative', className)}>
      <div
        className={cn(
          'flex cursor-text items-center gap-2 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-800 hover:border-gray-400 dark:border-gray-600 dark:bg-transparent dark:text-gray-100 dark:hover:border-gray-500',
          open && 'border-blue-500 ring-2 ring-blue-500',
          error && 'border-red-600 ring-2 ring-red-600',
          'h-8',
        )}
        onClick={handleInputClick}
      >
        <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={displayText}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onClickCapture={handleInputClickCapture}
          className={cn(
            'flex-1 bg-transparent text-gray-800 outline-none placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500',
            error && 'text-red-600',
          )}
          placeholder={placeholder}
        />
        <div className="border-l border-gray-200 pl-2 dark:border-gray-700">
          <TimezonePicker
            value={timezone}
            onChange={handleTimezoneChange}
            currentDateRange={value}
          />
        </div>
      </div>
    </div>
  );
};
